import React, { useCallback, useState, useEffect, ChangeEvent, useMemo } from 'react';
import { TextField, Paper, Button, Icon, FormControl, InputLabel, makeStyles, MenuItem, Select, FormHelperText, Checkbox, FormControlLabel, Theme }
    from '@material-ui/core';
import { TFunction } from 'i18next';
import clsx from 'clsx';
import produce from 'immer';
import { NumericMetricInfo, LogicalOperator, ComparisonOperator, MonitorCondition, MonitorTargetType, MonitorConditionThresholdType,
    MonitorConditionThreshold, RobotDefinition, MissingDataTreatment, RobotInfo } from '@moderepo/mode-apis';
import { useModeFormStyle, SelectInputOption, capitalizeAll, capitalizeFirst, getRobotDefinitionByRobotClass, TimeUnit }
    from '@moderepo/mode-app-components';
import { useEditMonitorStyle } from './EditMonitorStyle';
import * as RCUtils from '../../../containers/robot-cloud/RCUtils';
import { MonitorSetupInputErrors, EditMonitorStepProps } from './EditMonitorInfoStep';


const useStyle = makeStyles((theme: Theme) => {
    return {
        inputFieldWithUnitWrapper: {
            display      : 'flex',
            width        : '100%',
            flex         : 1,
            flexDirection: 'column',

            '& .input-and-unit': {
                display: 'flex',
                width  : '100%',
                flex   : 1,

                '& .input': {
                    width: '100%',
                },

                '& .unit': {
                    alignSelf : 'flex-start',
                    paddingTop: 20,
                },
            },


            '&.without-label': {
                '& .input-and-unit': {
                    '& .unit': {
                        paddingTop: 5,
                    },
                },
            },

            '& .helper-text': {
                flexBasis: '100%',
                flex     : 1,
            },
        },

        measurementCondition: {
            display      : 'flex',
            flexDirection: 'column',

            '& .form-control': {
                width: '100%',
            },

            '& .error': {
                flex     : 1,
                flexBasis: '100%',
            },

            [theme.breakpoints.up('sm')]: {
                flexDirection : 'row',
                justifyContent: 'space-between',
                flexWrap      : 'wrap',

                '& .form-control': {
                    width               : 'auto',
                    '&.measurement-name': {
                        flex : 1,
                        width: '100%',
                    },
                    '&:not(:last-child)': {
                        marginBottom: 0,
                    },
                    '&:not(:first-child)': {
                        marginLeft: theme.spacing(2),
                    },
                },
            },
        },


        measurementConditionOperator: {
            '& .MuiSelect-select': {
                paddingLeft : theme.spacing(2),
                paddingRight: theme.spacing(2),
                textAlign   : 'center',
            },
        },

        noMeasurementsError: {
            textAlign : 'center',
            fontWeight: 500,
            color     : theme.palette.error.main,
        },


        joinConditionsOperatorGroup: {
            padding                   : theme.spacing(0, 1.5),
            background                : 'none',
            display                   : 'flex',
            alignItems                : 'center',
            '& .add-condition-confirm': {
                fontWeight: '500',
                marginLeft: theme.spacing(2),
                fontSize  : 'larger',
            },
        },

        measurementValueUnit: {
            display                     : 'none',
            alignItems                  : 'flex-start',
            justifyContent              : 'flex-start',
            marginLeft                  : theme.spacing(1),
            marginTop                   : 25,
            minWidth                    : '3em',
            [theme.breakpoints.up('sm')]: {
                display: 'flex',
            },
        },


        measurementConditionThreshold: {
            display      : 'flex',
            flexDirection: 'column',

            '& .form-control': {
                width: '100%',
            },

            [theme.breakpoints.up('sm')]: {
                flexDirection : 'row',
                justifyContent: 'space-between',

                '& .form-control': {
                    width          : '100%',
                    flex           : 1,
                    '&:first-child': {
                        minWidth: 100,
                    },
                    '&:not(:first-child)': {
                        marginLeft: theme.spacing(2),
                    },
                    '&:not(:last-child)': {
                        marginBottom: 0,
                    },
                },
            },
        },

        missingDataSettings: {
            display      : 'flex',
            flexDirection: 'column',

            '& .form-control': {
                width: '100%',
            },

            [theme.breakpoints.up('sm')]: {
                flexDirection : 'row',
                justifyContent: 'space-between',
                flexWrap      : 'wrap',

                '& .form-control': {
                    flex           : 1,
                    width          : '100%',
                    '&:first-child': {
                        minWidth: 200,
                    },
                    '&:not(:first-child)': {
                        marginLeft: theme.spacing(2),
                    },
                },
            },
        },

    };
});



/**
 * Custom hook to create an Array of LogicalOperators used for Monitor Condition join select input field. These are AND/OR options
 * @param trans
 */
const useCreateLogicalOperatorOptions = (trans: TFunction): readonly SelectInputOption<LogicalOperator>[] => {
    const [options, setOptions] = useState<readonly SelectInputOption<LogicalOperator>[]>([]);

    useEffect(() => {
        setOptions(Object.values(LogicalOperator).map((operator: LogicalOperator): SelectInputOption<LogicalOperator> => {
            return {
                label: trans(RCUtils.getLogicalOperatorName(operator)).toUpperCase(),
                value: operator,
            };
        }));
    }, [trans]);

    return options;
};



/**
 * Custom hook to create an Array of ComparisonOperator used for comparing measurement value e.g. Less Than, Greater Than, Equal, etc...
 * @param trans
 */
const useCreateComparisonOperatorOptions = (trans: TFunction): readonly SelectInputOption<ComparisonOperator>[] => {
    const [options, setOptions] = useState<readonly SelectInputOption<ComparisonOperator>[]>([]);

    useEffect(() => {
        setOptions(Object.values(ComparisonOperator).map((operator: ComparisonOperator): SelectInputOption<ComparisonOperator> => {
            return {
                label: capitalizeAll(trans(RCUtils.getComparisonOperatorName(operator))),
                value: operator,
            };
        }));
    }, [trans]);

    return options;
};



/**
 * Custom hook to create an Array of measurement names the user can choose from to create a condition. We will get the list of measurement
 * names from the robot spec. Given the selected robotClass or deviceId, we can lookup the Robot definition and get the list of metrics'
 * value names and create an Array of measurement names based on those value names
 * @param trans
 */
const useCreateMeasurementNameOptions = (
    robotCatalog: readonly RobotDefinition[] | undefined, robotClass: string | undefined, trans: TFunction,
): readonly SelectInputOption<string>[] => {
    const [options, setOptions] = useState<readonly SelectInputOption<string>[]>([]);

    useEffect(() => {
        try {
            if (robotClass) {
                const robotDef = getRobotDefinitionByRobotClass(robotCatalog, robotClass);
                if (robotDef) {
                    setOptions(robotDef.numericMetrics.map((numericMetric: NumericMetricInfo): SelectInputOption<string> => {
                        return {
                            label: numericMetric.displayName || numericMetric.name,
                            value: numericMetric.name,
                        };
                    }));
                } else {
                    throw new Error('cannot find robot definition');
                }
            } else {
                throw new Error('no data');
            }
        } catch (error) {
            setOptions([]);
        }
    }, [trans, robotClass, robotCatalog]);

    return options;
};


/**
 * Custom hook to create an Array of Threshold types used for Monitor Condition threshold input field
 * @param trans
 */
const useCreateConditionThresholdTypeOptions = (trans: TFunction): readonly SelectInputOption<MonitorConditionThresholdType>[] => {
    const [options, setOptions] = useState<readonly SelectInputOption<MonitorConditionThresholdType>[]>([]);

    useEffect(() => {
        setOptions(Object.values(MonitorConditionThresholdType).map(
            (type: MonitorConditionThresholdType): SelectInputOption<MonitorConditionThresholdType> => {
                return {
                    label: capitalizeFirst(trans(RCUtils.getConditionThresholdTypeName(type))),
                    value: type,
                };
            },
        ));
    }, [trans]);

    return options;
};



/**
 * Custom hook to create an Array of Missing Data treatment types used for Monitor Condition missing data input field
 * @param trans
 */
const useCreateMissingDataTreatmentOptions = (trans: TFunction): readonly SelectInputOption<MissingDataTreatment>[] => {
    const [options, setOptions] = useState<readonly SelectInputOption<MissingDataTreatment>[]>([]);

    useEffect(() => {
        setOptions(Object.values(MissingDataTreatment).map(
            (type: MissingDataTreatment): SelectInputOption<MissingDataTreatment> => {
                return {
                    label: capitalizeFirst(trans(RCUtils.getMissingDataTreatmentTypeName(type))),
                    value: type,
                };
            },
        ));
    }, [trans]);

    return options;
};



interface ConditionInput {
    readonly valueName?: string;
    readonly operator?: ComparisonOperator;
    readonly value?: string;
}


interface ThresholdInput {
    type?: MonitorConditionThresholdType;
    parameters: {
        count?: string,
        duration?: string;
    };
}


const monitorConditionToArray = (monitorCondition: MonitorCondition): ConditionInput[] => {
    const leftCondition = monitorCondition.left ? {
        valueName: monitorCondition.left.measurement.valueName,
        operator : monitorCondition.left.operator,
        value    : monitorCondition.left.value.toString(),
    } : {
        valueName: '', operator: '' as ComparisonOperator, value: '',
    };

    const rightCondition = monitorCondition.right ? {
        valueName: monitorCondition.right.measurement.valueName,
        operator : monitorCondition.right.operator,
        value    : monitorCondition.right.value.toString(),
    } : {
        valueName: '', operator: '' as ComparisonOperator, value: '',
    };

    return [leftCondition, rightCondition];
};


const convertThresholdToInput = (threshold: MonitorConditionThreshold | undefined): ThresholdInput => {
    if (threshold && Object.values(MonitorConditionThresholdType).find((type: MonitorConditionThresholdType) => {
        return type === threshold.type;
    }) !== undefined) {
        return {
            type      : threshold.type,
            parameters: threshold.parameters || {},
        };
    }

    return {
        parameters: {},
    };
};


/**
 * Errors fields for Monitor Condition setup step
 */
interface MonitorConditionsInputErrors extends MonitorSetupInputErrors {
    leftCondition?: {
        joinOperator?: string;
        valueName?: string;
        operator?: string;
        value?: string;
    };
    rightCondition?: {
        joinOperator?: string;
        valueName?: string;
        operator?: string;
        value?: string;
    };
    threshold?: {
        type?: string;
        count?: string;
        duration?: string;
    };
    treatMissingDataAs?: string;
    sampleInterval?: string;
}



interface EditMonitorConditionsStepProps extends EditMonitorStepProps {
    readonly robotCatalog: readonly RobotDefinition[] | undefined;
    readonly targetRobotClass: string | undefined;
    readonly targetRobot: RobotInfo | undefined;
    readonly condition: MonitorCondition | undefined;
    readonly treatMissingDataAs: MissingDataTreatment | undefined;
    readonly sampleInterval: number | undefined;
    readonly onStepCompleted: (condition: MonitorCondition, missingDataTreatment: MissingDataTreatment, sampleInt: number)=> void;
}


/**
 * This is the HTML for the Setup Monitor Conditions step content
 * @param props
 */
export const EditMonitorConditionsStep = (props: EditMonitorConditionsStepProps) => {

    const { trans } = props;
    const formClasses = useModeFormStyle();
    const globalClasses = useEditMonitorStyle();
    const classes = useStyle();



    // These hold the current value of the user input. We will initialize these with the values from props

    // There are 2 Conditions and the values for each condition consist of valueName, compare operator, and value
    const [conditionsInput, setConditionsInput] = useState<ConditionInput[]>(props.condition ? monitorConditionToArray(props.condition) : [{ }, { }]);

    // Flag that specify whether or not to join another condition, OPTIONAL
    const [joinConditionsInput, setJoinConditionInput] = useState<boolean>(Boolean(props.condition?.operator));

    // LogicalOperator that join the conditions, OPTIONAL
    const [joinOperatorInput, setJoinOperatorInput] = useState<LogicalOperator | undefined>(props.condition?.operator || LogicalOperator.AND);

    const [thresholdInput, setThresholdInput] = useState<ThresholdInput>(
        convertThresholdToInput(props.condition?.threshold),
    );

    const [treatMissingDataAsInput, setTreatMissingDataAsInput] = useState<MissingDataTreatment | undefined>(props.treatMissingDataAs);
    const [sampleIntervalInput, setSampleIntervalInput] = useState<string | undefined>(props.sampleInterval?.toString());


    // Get the robotClass for the target. If targetType is robotClass then robotClass will be targetRobotClass. If targetType is RobotId then
    // we use the target robot's robotClass
    const robotClass = useMemo(() => {
        if (props.targetRobotClass) {
            return props.targetRobotClass;
        }
        if (props.targetRobot) {
            return props.targetRobot.robotClass;
        }
        return undefined;
    }, [props.targetRobotClass, props.targetRobot]);


    // These are the set of options used for the Dropdown/Select input
    const logicalOperatorOptions = useCreateLogicalOperatorOptions(trans);
    const compareOperatorOptions = useCreateComparisonOperatorOptions(trans);
    const measurementNameOptions = useCreateMeasurementNameOptions(props.robotCatalog, robotClass, trans);
    const conditionThresholdTypeOptions = useCreateConditionThresholdTypeOptions(trans);
    const missingDataTreatmentOptions = useCreateMissingDataTreatmentOptions(trans);
    const [inputErrors, setInputErrors] = useState<MonitorConditionsInputErrors>({ });



    /**
     * Validate Monitor Info form and dispatch step completed if success
     */
    const validateInputs = useCallback((dispatchOnComplete: boolean = true, dispatchOnError: boolean = true) => {

        const newInputErrors: MonitorConditionsInputErrors = {};


        // Get the input values
        const leftValueName = conditionsInput[0].valueName;
        const leftOperator = conditionsInput[0].operator;
        const leftValueStr = conditionsInput[0].value;
        const leftValue = leftValueStr ? Number(leftValueStr) : undefined;
        const thresholdType = thresholdInput.type;
        const thresholdCountStr = thresholdInput.parameters.count;
        const thresholdCountValue = thresholdCountStr ? Number(thresholdCountStr) : undefined;
        const thresholdDurationStr = thresholdInput.parameters.duration;
        const thresholdDurationValue = thresholdDurationStr && thresholdType === MonitorConditionThresholdType.DURATION_COUNT
            ? Number(thresholdDurationStr) : undefined;

        // NOTE: these are only available if joinConditionsOperator is NOT undefined
        const rightValueName = conditionsInput[1].valueName;
        const rightOperator = conditionsInput[1].operator;
        const rightValueStr = conditionsInput[1].value;
        const rightValue = rightValueStr ? Number(rightValueStr) : undefined;



        // First validate left condition input values because they are required
        if (!leftValueName) {
            newInputErrors.leftCondition = {
                ...newInputErrors.leftCondition,
                valueName: 'error.input.monitor_condition.invalid',
            };
        } else if (measurementNameOptions.find((option: SelectInputOption<string>) => {
            return option.value === leftValueName;
        }) === undefined) {
            newInputErrors.leftCondition = {
                ...newInputErrors.leftCondition,
                valueName: 'error.input.monitor_condition.invalid',
            };
        }
        if (!leftOperator) {
            newInputErrors.leftCondition = {
                ...newInputErrors.leftCondition,
                operator: 'error.input.monitor_condition.invalid',
            };
        }
        if (!leftValueStr) {
            newInputErrors.leftCondition = {
                ...newInputErrors.leftCondition,
                value: 'error.input.monitor_condition.invalid',
            };
        }

        if (joinConditionsInput) {
            // If user want to join multiple conditions, need to validate the right condition input values
            if (!joinOperatorInput) {
                newInputErrors.rightCondition = {
                    ...newInputErrors.rightCondition,
                    joinOperator: 'error.input.monitor_condition.invalid',
                };
            }
            if (!rightValueName) {
                newInputErrors.rightCondition = {
                    ...newInputErrors.rightCondition,
                    valueName: 'error.input.monitor_condition.invalid',
                };
            } else if (measurementNameOptions.find((option: SelectInputOption<string>) => {
                return option.value === rightValueName;
            }) === undefined) {
                newInputErrors.rightCondition = {
                    ...newInputErrors.rightCondition,
                    valueName: 'error.input.monitor_condition.invalid',
                };
            }
            if (!rightOperator) {
                newInputErrors.rightCondition = {
                    ...newInputErrors.rightCondition,
                    operator: 'error.input.monitor_condition.invalid',
                };
            }
            if (!rightValueStr) {
                newInputErrors.rightCondition = {
                    ...newInputErrors.rightCondition,
                    value: 'error.input.monitor_condition.invalid',
                };
            }
        }

        // validate the condition threshold input
        if (!thresholdType) {
            newInputErrors.threshold = {
                ...newInputErrors.threshold,
                type: 'error.input.monitor_condition_threshold.type.empty',
            };
        }
        if (!thresholdCountStr) {
            newInputErrors.threshold = {
                ...newInputErrors.threshold,
                count: 'error.input.monitor_condition_threshold.count.empty',
            };
        }
        if (thresholdCountValue !== undefined && (
            thresholdCountValue < RCUtils.MONITOR_CONDITION_THRESHOLD_COUNT_MIN || thresholdCountValue > RCUtils.MONITOR_CONDITION_THRESHOLD_COUNT_MAX
        )) {
            newInputErrors.threshold = {
                ...newInputErrors.threshold,
                count: 'error.input.monitor_condition_threshold.count.invalid',
            };
        }
        if (thresholdType === MonitorConditionThresholdType.DURATION_COUNT && !thresholdDurationStr) {
            newInputErrors.threshold = {
                ...newInputErrors.threshold,
                duration: 'error.input.monitor_condition_threshold.duration.empty',
            };
        }
        if (thresholdType === MonitorConditionThresholdType.DURATION_COUNT && thresholdDurationValue !== undefined && (
            thresholdDurationValue < RCUtils.MONITOR_CONDITION_THRESHOLD_DURATION_MIN
            || thresholdDurationValue > RCUtils.MONITOR_CONDITION_THRESHOLD_DURATION_MAX
        )) {
            newInputErrors.threshold = {
                ...newInputErrors.threshold,
                duration: 'error.input.monitor_condition_threshold.duration.invalid',
            };
        }


        // Validate missing data treatment and sample interval input
        if (!treatMissingDataAsInput) {
            newInputErrors.treatMissingDataAs = 'error.input.monitor_condition_treat_missing_data_as.empty';
        }

        const sampleInterval = sampleIntervalInput ? Number(sampleIntervalInput) : undefined;
        if (!sampleIntervalInput || sampleInterval === undefined) {
            newInputErrors.sampleInterval = 'error.input.monitor_condition_sample_interval.empty';
        } else if (sampleInterval < RCUtils.MONITOR_CONDITION_SAMPLE_INTERVAL_MIN || sampleInterval > RCUtils.MONITOR_CONDITION_SAMPLE_INTERVAL_MAX) {
            newInputErrors.sampleInterval = 'error.input.monitor_condition_sample_interval.invalid';
        }


        // Check if inputs have error
        const inputHasErrors = Object.keys(newInputErrors).length > 0;


        // All required values are provided, now need to check if conditions are valid and we only need to do this if both conditions depends
        // on the same measurement. If the 2 conditions are for different measurement, we don't need to validate the condition.
        if (!inputHasErrors && leftValue !== undefined && rightValue !== undefined && leftValueName === rightValueName) {
            // There are a couple of reasons the conditions are invalid
            // 1- Conditions are invalid if they are EXACTLY the same
            // 2- Conditions are invalid if they can NEVER be true or ALWAYS true

            if (leftValue === rightValue && leftOperator === rightOperator) {
                // 1- Check if conditions are exactly the same, if they have the same comparator and same value.
                newInputErrors.rightCondition = {
                    ...newInputErrors.rightCondition,
                    valueName: 'error.input.monitor_condition.duplicate',
                };
            } else {
                // 2- Check if the conditions can NEVER be true or ALWAYS true

                // First, sort the left and right conditions so that it is easier to compare. Make left condition value always
                // smaller than or equal the right condition value.
                const [leftCondition, rightCondition] = leftValue < rightValue ? [{
                    value   : leftValue,
                    operator: leftOperator,
                }, {
                    value   : rightValue,
                    operator: rightOperator,
                }] : [{
                    value   : rightValue,
                    operator: rightOperator,
                }, {
                    value   : leftValue,
                    operator: leftOperator,
                }];

                if (joinOperatorInput === LogicalOperator.AND) {
                    // Check for conditions that will NEVER be true. We have 5 comparison operators an 2 condition so there can be 25 possibilities
                    // combination and some of them are not valid. We need to check for invalid combinations.
                    //      x < 5 && x < 5      // valid e.g. x = 3
                    //      x < 5 && x <= 5     // valid e.g. x = 4
                    //      x < 5 && x == 5     // invalid
                    //      x < 5 && x >= 5     // invalid
                    //      x < 5 && x > 5      // invalid
                    //      x <= 5 && x < 5     // value e.g. x = 4
                    //      x <= 5 && x <= 5    // valid e.g. x = 2
                    //      x <= 5 && x == 5    // valid e.g. x = 5
                    //      x <= 5 && x >= 5    // valid e.g. x = 5
                    //      x <= 5 && x > 5     // invalid
                    //      x == 5 && x < 5     // invalid
                    //      x == 5 && x <= 5    // valid e.g. x = 5
                    //      x == 5 && x == 5    // valid e.g. x = 5
                    //      x == 5 && x >= 5    // valid e.g. x = 5
                    //      x == 5 && x > 5     // invalid
                    //      x >= 5 && x < 5     // invalid
                    //      x >= 5 && x <= 5    // valid e.g. x = 5
                    //      x >= 5 && x == 5    // valid e.g x = 5
                    //      x >= 5 && x >= 5    // valid e.g. x = 5
                    //      x >= 5 && x > 5     // valid e.g. x = 10
                    //      x > 5 && x < 5      // invalid
                    //      x > 5 && x <= 5     // invalid
                    //      x > 5 && x == 5     // invalid
                    //      x > 5 && x >= 5     // valid e.g x = 10
                    //      x > 5 && x > 5      // valid e.g x = 6

                    // Same combination but right condition value is larger than left condition value
                    //      x < 5 && x < 6      // valid e.g. x = 3
                    //      x < 5 && x <= 6     // valid e.g. x = 4
                    //      x < 5 && x == 6     // invalid
                    //      x < 5 && x >= 6     // invalid
                    //      x < 5 && x > 6      // invalid
                    //      x <= 5 && x < 6     // value e.g. x = 4
                    //      x <= 5 && x <= 6    // valid e.g. x = 2
                    //      x <= 5 && x == 6    // invalid
                    //      x <= 5 && x >= 6    // invalid
                    //      x <= 5 && x > 6     // invalid
                    //      x == 5 && x < 6     // valid e.g. x = 5
                    //      x == 5 && x <= 6    // valid e.g. x = 5
                    //      x == 5 && x == 6    // invalid
                    //      x == 5 && x >= 6    // invalid
                    //      x == 5 && x > 6     // invalid
                    //      x >= 5 && x < 6     // valid e.g. x = 5
                    //      x >= 5 && x <= 6    // valid e.g. x = 5
                    //      x >= 5 && x == 6    // valid e.g x = 6
                    //      x >= 5 && x >= 6    // valid e.g. x = 6
                    //      x >= 5 && x > 6     // valid e.g. x = 7
                    //      x > 5 && x < 6      // valid e.g. 5.5
                    //      x > 5 && x <= 6     // valid e.g. 6
                    //      x > 5 && x == 6     // valid e.g. x = 6
                    //      x > 5 && x >= 6     // valid e.g x = 10
                    //      x > 5 && x > 6      // valid e.g x = 7
                    // Since we already sorted the conditions' values, the left value is always going to be less then or equal the right value
                    // so we just need to disallow these combination of comparators between left and right conditions, no need to compare the values.
                    // Create a list of disallowed combination of operators based on the list of INVALID combinations
                    const disallowedOperatorCombinations = [
                        [ComparisonOperator.LESS_THAN, ComparisonOperator.EQUAL, false],
                        [ComparisonOperator.LESS_THAN, ComparisonOperator.GREATER_THAN_OR_EQUAL, false],
                        [ComparisonOperator.LESS_THAN, ComparisonOperator.GREATER_THAN, false],
                        [ComparisonOperator.LESS_THAN_OR_EQUAL, ComparisonOperator.GREATER_THAN, false],
                        [ComparisonOperator.EQUAL, ComparisonOperator.LESS_THAN, false],
                        [ComparisonOperator.EQUAL, ComparisonOperator.GREATER_THAN, false],
                        [ComparisonOperator.GREATER_THAN_OR_EQUAL, ComparisonOperator.LESS_THAN, false],
                        [ComparisonOperator.GREATER_THAN, ComparisonOperator.LESS_THAN, false],
                        [ComparisonOperator.GREATER_THAN, ComparisonOperator.LESS_THAN_OR_EQUAL, false],
                        [ComparisonOperator.GREATER_THAN, ComparisonOperator.EQUAL, false],
                        [ComparisonOperator.LESS_THAN, ComparisonOperator.EQUAL, true],
                        [ComparisonOperator.LESS_THAN, ComparisonOperator.GREATER_THAN_OR_EQUAL, true],
                        [ComparisonOperator.LESS_THAN, ComparisonOperator.GREATER_THAN, true],
                        [ComparisonOperator.LESS_THAN_OR_EQUAL, ComparisonOperator.EQUAL, true],
                        [ComparisonOperator.LESS_THAN_OR_EQUAL, ComparisonOperator.GREATER_THAN_OR_EQUAL, true],
                        [ComparisonOperator.LESS_THAN_OR_EQUAL, ComparisonOperator.GREATER_THAN, true],
                        [ComparisonOperator.EQUAL, ComparisonOperator.EQUAL, true],
                        [ComparisonOperator.EQUAL, ComparisonOperator.GREATER_THAN_OR_EQUAL, true],
                        [ComparisonOperator.EQUAL, ComparisonOperator.GREATER_THAN, true],
                    ];
                    if (disallowedOperatorCombinations.find((combo: any) => {
                        return combo[0] === leftCondition.operator
                            && combo[1] === rightCondition.operator
                            && combo[2] === (leftCondition.value < rightCondition.value);
                    })) {
                        newInputErrors.rightCondition = {
                            ...newInputErrors.rightCondition,
                            valueName: 'error.input.monitor_condition.never_true',
                        };
                    }
                } else if (joinOperatorInput === LogicalOperator.OR) {
                    // Check for conditions that are ALWAYS be true and prevent them. We also have 25 combinations and some are invalid. However,
                    // unlike AND join operator, the invalid combinations are combinations which we CAN NOT find a value for x that does not
                    // satisfy the condition.
                    //      x < 5 || x < 5      // valid e.g. x = 10
                    //      x < 5 || x <= 5     // valid e.g. x = 10
                    //      x < 5 || x == 5     // valid e.g. x = 6
                    //      x < 5 || x >= 5     // invalid
                    //      x < 5 || x > 5      // valid e.g x = 5
                    //      x <= 5 || x < 5     // valid e.g. x = 6
                    //      x <= 5 || x <= 5    // valid e.g. x = 6
                    //      x <= 5 || x == 5    // valid e.g. x = 6
                    //      x <= 5 || x >= 5    // invalid
                    //      x <= 5 || x > 5     // invalid
                    //      x == 5 || x < 5     // valid e.g. x = 6
                    //      x == 5 || x <= 5    // valid e.g. x = 6
                    //      x == 5 || x == 5    // valid e.g x = 6
                    //      x == 5 || x >= 5    // valid e.g. 4
                    //      x == 5 || x > 5     // valid e.g. x = 4
                    //      x >= 5 || x < 5     // invalid
                    //      x >= 5 || x <= 5    // invalid
                    //      x >= 5 || x == 5    // valid e.g. x = 4
                    //      x >= 5 || x >= 5    // valid e.g. x = 4
                    //      x >= 5 || x > 5     // valid e.g. x = 4
                    //      x > 5 || x < 5      // valid e.g. x = 5
                    //      x > 5 || x <= 5     // invalid
                    //      x > 5 || x == 5     // valid e.g. x = 4
                    //      x > 5 || x >= 5     // valid e.g. x = 4
                    //      x > 5 || x > 5      // valid e.g. x = 4

                    // Same combination but right condition value is larger than left condition value
                    //      x < 5 || x < 6      // valid e.g. x = 7
                    //      x < 5 || x <= 6     // valid e.g. x = 7
                    //      x < 5 || x == 6     // valid e.g. x = 7
                    //      x < 5 || x >= 6     // valid e.g. x = 5
                    //      x < 5 || x > 6      // valid e.g. x = 5.5
                    //      x <= 5 || x < 6     // valid e.g. x = 7
                    //      x <= 5 || x <= 6    // valid e.g. x = 7
                    //      x <= 5 || x == 6    // valid e.g. x = 7
                    //      x <= 5 || x >= 6    // valid e.g. x = 5.5
                    //      x <= 5 || x > 6     // valid e.g. x = 6
                    //      x == 5 || x < 6     // valid e.g. x = 7
                    //      x == 5 || x <= 6    // valid e.g. x = 7
                    //      x == 5 || x == 6    // valid e.g. x = 7
                    //      x == 5 || x >= 6    // valid e.g. x = 4
                    //      x == 5 || x > 6     // valid e.g. x = 4
                    //      x >= 5 || x < 6     // invalid
                    //      x >= 5 || x <= 6    // invalid
                    //      x >= 5 || x == 6    // valid e.g. x = 4
                    //      x >= 5 || x >= 6    // valid e.g. x = 4
                    //      x >= 5 || x > 6     // valid e.g. x = 4
                    //      x > 5 || x < 6      // invalid
                    //      x > 5 || x <= 6     // invalid
                    //      x > 5 || x == 6     // valid e.g. x = 4
                    //      x > 5 || x >= 6     // valid e.g. x = 4
                    //      x > 5 || x > 6      // valid e.g. x = 4
                    const disallowedOperatorCombinations = [
                        [ComparisonOperator.LESS_THAN, ComparisonOperator.GREATER_THAN_OR_EQUAL, false],
                        [ComparisonOperator.LESS_THAN_OR_EQUAL, ComparisonOperator.GREATER_THAN_OR_EQUAL, false],
                        [ComparisonOperator.LESS_THAN_OR_EQUAL, ComparisonOperator.GREATER_THAN, false],
                        [ComparisonOperator.GREATER_THAN_OR_EQUAL, ComparisonOperator.LESS_THAN, false],
                        [ComparisonOperator.GREATER_THAN_OR_EQUAL, ComparisonOperator.LESS_THAN_OR_EQUAL, false],
                        [ComparisonOperator.GREATER_THAN, ComparisonOperator.LESS_THAN_OR_EQUAL, false],
                        [ComparisonOperator.GREATER_THAN_OR_EQUAL, ComparisonOperator.LESS_THAN, true],
                        [ComparisonOperator.GREATER_THAN_OR_EQUAL, ComparisonOperator.LESS_THAN_OR_EQUAL, true],
                        [ComparisonOperator.GREATER_THAN, ComparisonOperator.LESS_THAN, true],
                        [ComparisonOperator.GREATER_THAN, ComparisonOperator.LESS_THAN_OR_EQUAL, true],
                    ];
                    if (disallowedOperatorCombinations.find((combo: any) => {
                        return combo[0] === leftCondition.operator
                            && combo[1] === rightCondition.operator
                            && combo[2] === (leftCondition.value < rightCondition.value);
                    })) {
                        newInputErrors.rightCondition = {
                            ...newInputErrors.rightCondition,
                            valueName: 'error.input.monitor_condition.always_true',
                        };
                    }
                }
            }
        }


        // Now check for error AGAIN after we validated the logic that make sure the conditions are possible e.g. NOT always true or NEVER true, etc..
        const hasErrors = Object.keys(newInputErrors).length > 0;



        // Update Local input errors
        setInputErrors(newInputErrors);

        // If 'dispatchOnError' is specified, dispatch the error or null to the container
        if (dispatchOnError) {
            props.onStepError(hasErrors);
        }


        if (!hasErrors && dispatchOnComplete) {
            // Only dispatch onComplete if dispatchOnComplete is true. Sometime we just want to validate the input but not submit the form.

            if (robotClass) {
                const robotDef = getRobotDefinitionByRobotClass(props.robotCatalog, robotClass);
                if (robotDef) {
                    props.onStepCompleted({
                        left: {
                            measurement: {
                                valueName: leftValueName,
                            },
                            operator: leftOperator,
                            value   : leftValue,
                        },
                        operator: joinConditionsInput && joinOperatorInput ? joinOperatorInput : '',
                        right   : joinConditionsInput && joinOperatorInput ? {
                            measurement: {
                                valueName: rightValueName,
                            },
                            operator: rightOperator,
                            value   : rightValue,
                        } : undefined,
                        threshold: {
                            type      : thresholdType,
                            parameters: {
                                count   : thresholdCountValue,
                                duration: thresholdDurationValue,
                            },
                        },
                    } as MonitorCondition,
                    treatMissingDataAsInput as MissingDataTreatment,
                    sampleInterval as number);
                }
            }
        }


        return !hasErrors;
    }, [props, conditionsInput, joinOperatorInput, robotClass, measurementNameOptions, thresholdInput, treatMissingDataAsInput,
        sampleIntervalInput, joinConditionsInput]);


    // Create and cache a function that return the arrow icon to use for the Select component.
    const selectArrowIcon = useCallback(() => {
        return (
            <></>
        );
    }, []);


    /**
     * This will create the HTML for ONE condition. Each condition is consists of a measurement name, a compare operator, and a measurement value.
     * Because we can have 2 conditions so instead of duplicating this block of code twice, we moved this block of code into a separate function
     * and can just call it twice to create a left condition and/or right condition.
     */
    const renderMonitorCondition = useCallback((conditionIndex: number) => {
        const { valueName } = conditionsInput[conditionIndex];

        // find the metric for the selected measurementName and get the unit
        const robotDef = getRobotDefinitionByRobotClass(props.robotCatalog, robotClass);
        const unit = valueName && robotDef ? robotDef.numericMetrics.find((m: NumericMetricInfo) => {
            return m.name === valueName;
        })?.unit : '';

        const errors = conditionIndex === 0 ? inputErrors.leftCondition : inputErrors.rightCondition;

        return (
            <Paper elevation={1} className={clsx(classes.measurementCondition, globalClasses.formControlGroup)}>
                {conditionIndex > 0 && (
                    <FormControl
                        className={clsx(globalClasses.formControl, 'form-control')}
                        variant="standard"
                        error={Boolean(errors?.joinOperator)}
                    >
                        <InputLabel shrink>
                            &nbsp;
                        </InputLabel>
                        <Select
                            displayEmpty
                            value={joinOperatorInput || ''}
                            onChange={(event: ChangeEvent<{value: unknown}>) => {
                                // Input changed so clear the error and update the state
                                props.onStepError(false);
                                setJoinOperatorInput(event.target.value as LogicalOperator);
                            }}
                        >
                            {logicalOperatorOptions.map((option: SelectInputOption<LogicalOperator>) => {
                                return (
                                    <MenuItem key={option.label} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                )}

                <FormControl
                    className={clsx(globalClasses.formControl, 'form-control', 'measurement-name')}
                    fullWidth
                    required
                    error={Boolean(errors?.valueName)}
                >
                    <InputLabel shrink>
                        {capitalizeAll(trans('input.monitor_condition_measurement_name.label'))}
                    </InputLabel>
                    <Select
                        displayEmpty
                        value={conditionsInput[conditionIndex].valueName || ''}
                        onChange={(event: ChangeEvent<{value: unknown}>) => {
                            // Input changed so clear the error and update the state
                            props.onStepError(false);

                            setInputErrors(produce(inputErrors, (draft: any) => {
                                if (conditionIndex === 0 && draft.leftCondition) {
                                    delete draft.leftCondition.valueName;
                                } else if (conditionIndex === 1 && draft.rightCondition) {
                                    delete draft.rightCondition.valueName;
                                }
                            }));

                            setConditionsInput(produce(conditionsInput, (draft: any) => {
                                draft[conditionIndex].valueName = event.target.value as MonitorTargetType;
                            }));
                        }}
                        onBlur={() => {
                            // on done editing, validate input but don't submit it until the user click next
                            // validateInputs(false);
                        }}
                    >
                        <MenuItem key="empty" value="" disabled>
                            {capitalizeFirst(trans('label.select_one'))}
                        </MenuItem>
                        {measurementNameOptions.map((option: SelectInputOption<string>) => {
                            return (
                                <MenuItem key={option.label} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
                <FormControl
                    className={clsx(globalClasses.formControl, 'form-control')}
                    variant="standard"
                    error={Boolean(errors?.operator)}
                >
                    <InputLabel shrink />
                    <Select
                        className={classes.measurementConditionOperator}
                        displayEmpty
                        value={conditionsInput[conditionIndex].operator || ''}
                        IconComponent={selectArrowIcon}
                        onChange={(event: ChangeEvent<{value: unknown}>) => {
                            // Input changed so clear the error and update the state
                            props.onStepError(false);

                            setInputErrors(produce(inputErrors, (draft: any) => {
                                if (conditionIndex === 0 && draft.leftCondition) {
                                    delete draft.leftCondition.operator;
                                } else if (conditionIndex === 1 && draft.rightCondition) {
                                    delete draft.rightCondition.operator;
                                }
                            }));

                            setConditionsInput(produce(conditionsInput, (draft: any) => {
                                draft[conditionIndex].operator = event.target.value as ComparisonOperator;
                            }));
                        }}
                        onBlur={() => {
                            // on done editing, validate input but don't submit it until the user click next
                            // validateInputs(false);
                        }}
                    >
                        <MenuItem key="empty" value="" disabled>
                            ???
                        </MenuItem>
                        {compareOperatorOptions.map((option: SelectInputOption<ComparisonOperator>) => {
                            return (
                                <MenuItem key={option.label} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
                <div className={clsx(classes.inputFieldWithUnitWrapper, formClasses.formInputField, globalClasses.formControl, 'form-control')}>
                    <div className="input-and-unit">
                        <TextField
                            variant="standard"
                            className="input"
                            type="number"
                            required
                            autoFocus
                            label={capitalizeAll(trans('input.monitor_condition_measurement_value.label'))}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            value={conditionsInput[conditionIndex].value || ''}
                            error={Boolean(errors?.value)}
                            onChange={(event: React.FormEvent) => {
                                // Input changed so clear the error and update the state
                                props.onStepError(false);

                                setInputErrors(produce(inputErrors, (draft: any) => {
                                    if (conditionIndex === 0 && draft.leftCondition) {
                                        delete draft.leftCondition.value;
                                    } else if (conditionIndex === 1 && draft.rightCondition) {
                                        delete draft.rightCondition.value;
                                    }
                                }));

                                setConditionsInput(produce(conditionsInput, (draft: any) => {
                                    draft[conditionIndex].value = (event.currentTarget as HTMLSelectElement).value;
                                }));
                            }}
                            onBlur={() => {
                                // on done editing, validate input but don't submit it until the user click next
                                // validateInputs(false);
                            }}
                        />
                        <div className="unit">
                            {unit}
                        </div>
                    </div>
                </div>
                {errors && (
                    <FormHelperText className="error" error={Boolean(errors)}>
                        {trans(errors?.joinOperator || errors?.valueName || errors?.operator || errors?.value || '')}
                    </FormHelperText>
                )}
            </Paper>
        );

    }, [globalClasses, classes, trans, props, formClasses, compareOperatorOptions, conditionsInput, measurementNameOptions, robotClass,
        inputErrors, joinOperatorInput, logicalOperatorOptions, selectArrowIcon]);



    /**
     * Render the UI for Threshold settings
     */
    const renderConditionThreshold = useCallback(() => {

        return (
            <Paper elevation={1} className={clsx(classes.measurementConditionThreshold, globalClasses.formControlGroup)}>
                <div className={clsx(classes.inputFieldWithUnitWrapper, 'without-label',
                    formClasses.formInputField, globalClasses.formControl, 'form-control')}
                >
                    <div className="input-and-unit">
                        <TextField
                            className="input"
                            variant="standard"
                            type="number"
                            required
                            autoFocus
                            InputLabelProps={{
                                shrink: true,
                            }}
                            InputProps={{
                                inputProps: {
                                    min: RCUtils.MONITOR_CONDITION_THRESHOLD_COUNT_MIN,
                                    max: RCUtils.MONITOR_CONDITION_THRESHOLD_COUNT_MAX,
                                },
                            }}
                            error={Boolean(inputErrors.threshold?.count)}
                            value={thresholdInput.parameters.count || ''}
                            onChange={(event: React.FormEvent) => {
                                // Input changed so clear the error and update the state
                                props.onStepError(false);

                                setInputErrors(produce(inputErrors, (draft: any) => {
                                    if (draft.threshold) {
                                        delete draft.threshold.count;
                                    }
                                }));

                                setThresholdInput(produce(thresholdInput, (draft: any) => {
                                    draft.parameters.count = (event.currentTarget as HTMLSelectElement).value;
                                }));
                            }}
                            onBlur={() => {
                                // on done editing, validate input but don't submit it until the user click next
                                // validateInputs(false);
                            }}
                        />
                        <div className="unit">
                            {trans('input.monitor_condition_threshold_count.label')}
                        </div>
                    </div>
                    <FormHelperText
                        className={clsx('helper-text', inputErrors.threshold?.count && 'error')}
                        error={Boolean(inputErrors.threshold?.count)}
                    >
                        {inputErrors.threshold?.count
                            ? trans(inputErrors.threshold?.count)
                            : capitalizeFirst(trans('input.generic_input_range.help_text', {
                                min: RCUtils.MONITOR_CONDITION_THRESHOLD_COUNT_MIN, max: RCUtils.MONITOR_CONDITION_THRESHOLD_COUNT_MAX,
                            }))}
                    </FormHelperText>
                </div>

                <FormControl
                    className={clsx(globalClasses.formControl, 'form-control')}
                    required
                    error={Boolean(inputErrors.threshold?.type)}
                >
                    <Select
                        displayEmpty
                        value={thresholdInput.type || ''}
                        onChange={(event: ChangeEvent<{value: unknown}>) => {
                            // Input changed so clear the error and update the state
                            props.onStepError(false);

                            setInputErrors(produce(inputErrors, (draft: any) => {
                                if (draft.threshold) {
                                    delete draft.threshold.type;
                                }
                            }));

                            setThresholdInput(produce(thresholdInput, (draft: any) => {
                                draft.type = event.target.value as MonitorConditionThresholdType;
                            }));
                        }}
                        onBlur={() => {
                            // on done editing, validate input but don't submit it until the user click next
                            // validateInputs(false);
                        }}
                    >
                        <MenuItem key="empty" value="" disabled>
                            {capitalizeFirst(trans('label.select_one'))}
                        </MenuItem>
                        {conditionThresholdTypeOptions.map((option: SelectInputOption<MonitorConditionThresholdType>) => {
                            return (
                                <MenuItem key={option.label} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            );
                        })}
                    </Select>
                    {inputErrors.threshold?.type && (
                        <FormHelperText>
                            {trans(inputErrors.threshold.type)}
                        </FormHelperText>
                    )}
                </FormControl>

                {thresholdInput.type === MonitorConditionThresholdType.DURATION_COUNT && (
                    <div className={clsx(classes.inputFieldWithUnitWrapper, 'without-label',
                        formClasses.formInputField, globalClasses.formControl, 'form-control')}
                    >
                        <div className="input-and-unit">
                            <TextField
                                className="input"
                                variant="standard"
                                type="number"
                                required
                                autoFocus
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                InputProps={{
                                    inputProps: {
                                        min: RCUtils.MONITOR_CONDITION_THRESHOLD_DURATION_MIN,
                                        max: RCUtils.MONITOR_CONDITION_THRESHOLD_DURATION_MAX,
                                    },
                                }}
                                value={thresholdInput.parameters.duration || ''}
                                onChange={(event: React.FormEvent) => {
                                    // Input changed so clear the error and update the state
                                    props.onStepError(false);

                                    setInputErrors(produce(inputErrors, (draft: any) => {
                                        if (draft.threshold) {
                                            delete draft.threshold.duration;
                                        }
                                    }));

                                    setThresholdInput(produce(thresholdInput, (draft: any) => {
                                        draft.parameters.duration = (event.currentTarget as HTMLSelectElement).value;
                                    }));
                                }}
                                onBlur={() => {
                                    // on done editing, validate input but don't submit it until the user click next
                                    // validateInputs(false);
                                }}
                            />
                            <div className="unit">
                                {trans(TimeUnit.SECOND, { count: 2 })}
                            </div>
                        </div>
                        <FormHelperText
                            className={clsx('helper-text', Boolean(inputErrors.threshold?.duration) && 'error')}
                            error={Boolean(inputErrors.threshold?.duration)}
                        >
                            {inputErrors.threshold?.duration
                                ? trans(inputErrors.threshold?.duration)
                                : capitalizeFirst(trans('input.generic_input_range.help_text', {
                                    min: RCUtils.MONITOR_CONDITION_THRESHOLD_DURATION_MIN, max: RCUtils.MONITOR_CONDITION_THRESHOLD_DURATION_MAX,
                                }))}
                        </FormHelperText>
                    </div>
                )}

            </Paper>
        );
    }, [conditionThresholdTypeOptions, thresholdInput, classes, formClasses, globalClasses, props, trans, inputErrors]);



    /**
     * Render the UI for Missing Data settings
     */
    const renderMissingDataSettings = useCallback(() => {

        return (
            <Paper elevation={1} className={clsx(classes.missingDataSettings, globalClasses.formControlGroup)}>
                <div className={clsx(classes.inputFieldWithUnitWrapper, formClasses.formInputField, globalClasses.formControl, 'form-control')}>
                    <div className="input-and-unit">
                        <TextField
                            className="input"
                            variant="standard"
                            required
                            type="number"
                            autoFocus
                            label={capitalizeAll(trans('input.monitor_condition_sample_interval.label'))}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            InputProps={{
                                inputProps: {
                                    min: RCUtils.MONITOR_CONDITION_SAMPLE_INTERVAL_MIN,
                                    max: RCUtils.MONITOR_CONDITION_SAMPLE_INTERVAL_MAX,
                                },
                            }}
                            value={sampleIntervalInput !== undefined ? sampleIntervalInput : ''}
                            onChange={(event: React.FormEvent) => {
                                // Input changed so clear the error and update the state
                                props.onStepError(false);

                                setInputErrors(produce(inputErrors, (draft: any) => {
                                    delete draft.sampleInterval;
                                }));

                                setSampleIntervalInput((event.currentTarget as HTMLInputElement).value);
                            }}
                            onBlur={() => {
                                // on done editing, validate input but don't submit it until the user click next
                                // validateInputs(false);
                            }}
                        />
                        <div className="unit">
                            {trans(TimeUnit.SECOND, { count: 2 })}
                        </div>
                    </div>
                    <FormHelperText
                        className={clsx('helper-text', Boolean(inputErrors.sampleInterval) && 'error')}
                        error={Boolean(inputErrors.sampleInterval)}
                    >
                        {inputErrors.sampleInterval
                            ? trans(inputErrors.sampleInterval)
                            : capitalizeFirst(trans('input.generic_input_range.help_text', {
                                min: RCUtils.MONITOR_CONDITION_SAMPLE_INTERVAL_MIN, max: RCUtils.MONITOR_CONDITION_SAMPLE_INTERVAL_MAX,
                            }))}
                    </FormHelperText>
                </div>

                <FormControl
                    className={clsx(globalClasses.formControl, 'form-control')}
                    required
                    error={Boolean(inputErrors.treatMissingDataAs)}
                >
                    <InputLabel shrink>
                        {capitalizeAll(trans('input.monitor_condition_treat_missing_data_as.label'))}
                    </InputLabel>
                    <Select
                        displayEmpty
                        value={treatMissingDataAsInput || ''}
                        onChange={(event: ChangeEvent<{value: unknown}>) => {
                            // Input changed so clear the error and update the state
                            props.onStepError(false);

                            setInputErrors(produce(inputErrors, (draft: any) => {
                                delete draft.treatMissingDataAs;
                            }));

                            setTreatMissingDataAsInput(event.target.value as MissingDataTreatment);
                        }}
                        onBlur={() => {
                            // on done editing, validate input but don't submit it until the user click next
                            // validateInputs(false);
                        }}
                    >
                        <MenuItem key="empty" value="" disabled>
                            {capitalizeFirst(trans('label.select_one'))}
                        </MenuItem>
                        {missingDataTreatmentOptions.map((option: SelectInputOption<MissingDataTreatment>) => {
                            return (
                                <MenuItem key={option.label} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            );
                        })}
                    </Select>
                    {inputErrors.treatMissingDataAs && (
                        <FormHelperText>
                            {trans(inputErrors.treatMissingDataAs)}
                        </FormHelperText>
                    )}
                </FormControl>

            </Paper>
        );
    }, [treatMissingDataAsInput, sampleIntervalInput, missingDataTreatmentOptions, classes, formClasses, globalClasses, props, trans, inputErrors]);



    return (
        <div className={globalClasses.stepContentBody}>
            {measurementNameOptions.length > 0 && (
                <>
                    <div className={clsx(globalClasses.stepContentForm)}>
                        <div className={clsx(globalClasses.stepSubtitle)}>
                            {capitalizeFirst(trans('create_monitor.step_conditions.subtitle'))}
                        </div>
                        {renderMonitorCondition(0)}

                        <div className={clsx(globalClasses.formControlGroup, classes.joinConditionsOperatorGroup)}>
                            <FormControlLabel
                                className={clsx(globalClasses.formControl, 'form-control')}
                                control={(
                                    <Checkbox
                                        value={joinConditionsInput}
                                        checked={joinConditionsInput}
                                        color="primary"
                                        onChange={(event: any) => {
                                            // Input changed so clear the error and update the state
                                            props.onStepError(false);

                                            setJoinConditionInput(event.target.checked);
                                        }}
                                        onBlur={() => {
                                            // on done editing, validate input but don't submit it until the user click next
                                            // validateInputs(false);
                                        }}
                                    />
                                )}
                                label={capitalizeFirst(trans('create_monitor.step_conditions.join_condition_confirmation'))}
                                labelPlacement="end"
                            />
                        </div>

                        {/* If conditions join operator is AND/OR, show right condition */}
                        {joinConditionsInput && (
                            renderMonitorCondition(1)
                        )}
                    </div>

                    <div className={clsx(globalClasses.stepContentForm)}>
                        <div className={clsx(globalClasses.stepSubtitle)}>
                            {capitalizeFirst(trans('create_monitor.step_conditions_threshold.subtitle'))}
                        </div>
                        {renderConditionThreshold()}
                    </div>

                    <div className={clsx(globalClasses.stepContentForm)}>
                        <div className={clsx(globalClasses.stepSubtitle)}>
                            {capitalizeFirst(trans('create_monitor.step_conditions_missing_data.subtitle'))}
                        </div>
                        {renderMissingDataSettings()}
                    </div>
                </>
            )}

            {measurementNameOptions.length === 0 && (
                <div className={classes.noMeasurementsError}>
                    {capitalizeFirst(trans('create_monitor.step_conditions.missing_measurements'))}
                </div>
            )}

            <div className={globalClasses.stepContentActionsContainer}>
                <Button
                    disabled={!props.canGoBack}
                    onClick={() => {
                        // Validate the inputs AND dispatch onComplete if successful so that we can save the user inputs before going back.
                        // However, if inputs are invalid, we will not dispatch error and just take the user back to the previous step. We will not
                        // save the user's input since it is invalid anyway.
                        validateInputs(true, false);
                        props.onPrevStepClick();
                    }}
                    className={globalClasses.navButton}
                    startIcon={<Icon>navigate_before</Icon>}
                >
                    {capitalizeAll(trans('label.back'))}
                </Button>
                <Button
                    onClick={() => {
                        if (validateInputs(true)) {
                            props.onNextStepClick();
                        }
                    }}
                    className={globalClasses.navButton}
                    endIcon={<Icon>navigate_next</Icon>}
                >
                    {capitalizeAll(trans(props.isLastStep ? 'label.finish' : 'label.next'))}
                </Button>
                {!props.isNewMonitor && (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                            if (props.onGotoLastStepClick && validateInputs(true)) {
                                props.onGotoLastStepClick();
                            }
                        }}
                        className={clsx(globalClasses.navButton, 'finish')}
                    >
                        {capitalizeAll(trans('label.finish'))}
                    </Button>
                )}
            </div>
        </div>
    );
};
